import React, { useState, useEffect } from 'react';
import { BrowserRouter, useLocation, Routes, Route } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AppRoutes from './routes/AppRoutes';
import Navbar from './components/layout/Navbar';
import Sidebar from './components/layout/Sidebar';
import Login from './pages/Login';
import ToastContainer from './components/organisms/ToastContainer';
import { lazy, Suspense } from 'react';
import Spinner from './components/atoms/Spinner';
import { initializeAuth, logout } from './features/auth/authSlice';
import { useSignalR } from './hooks/useSignalR';

const SignaturePage    = lazy(() => import('./pages/SignaturePage'));
const ChangePassword  = lazy(() => import('./pages/ChangePassword'));

// Purge des anciennes clés localStorage (migration vers cookies HttpOnly)
const purgeOldLocalStorage = () => {
  ['authToken', 'authUser'].forEach((key) => localStorage.removeItem(key));
};

const AppShell = () => {
  const user = useSelector(state => state.auth.user);
  const initializing = useSelector(state => state.auth.initializing);
  const dispatch = useDispatch();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const location = useLocation();

  // Connexion SignalR globale — démarre quand l'user est connecté, s'arrête à la déconnexion
  useSignalR();

  useEffect(() => {
    // Supprimer les anciens tokens du localStorage une fois pour toutes
    purgeOldLocalStorage();
    // Restaurer la session via le cookie HttpOnly
    dispatch(initializeAuth());
  }, [dispatch]);

  // Écoute l'événement déclenché par api.js quand le refresh échoue
  useEffect(() => {
    const handler = () => dispatch(logout());
    window.addEventListener('auth:expired', handler);
    return () => window.removeEventListener('auth:expired', handler);
  }, [dispatch]);

  // ── Route publique : page de signature ──────────────────────────────────────
  if (/^\/sign\/.+/.test(location.pathname)) {
    return (
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Spinner size="lg" /></div>}>
        <Routes>
          <Route path="/sign/:token" element={<SignaturePage />} />
        </Routes>
      </Suspense>
    );
  }

  // Pendant la vérification initiale du cookie → écran de chargement neutre
  if (initializing) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!user) return <Login />;

  // Première connexion : changement de mot de passe obligatoire, hors layout
  if (user.forcePasswordChange) {
    return (
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Spinner size="lg" /></div>}>
        <ChangePassword />
      </Suspense>
    );
  }

  return (
    <div className="flex bg-gray-50" style={{ height: '100dvh' }}>
      {/* Sidebar desktop */}
      <div className="hidden md:flex flex-shrink-0">
        <Sidebar onClose={() => setSidebarOpen(false)} />
      </div>

      {/* Overlay sidebar mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar mobile (drawer) */}
      <div className={`fixed inset-y-0 ${isRTL ? 'right-0' : 'left-0'} z-40 w-60 transform transition-transform md:hidden ${sidebarOpen ? 'translate-x-0' : isRTL ? 'translate-x-full' : '-translate-x-full'}`}>
        <Sidebar onClose={() => setSidebarOpen(false)} />
      </div>

      {/* Contenu principal */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Navbar fixée en haut — z-20 pour passer au-dessus du contenu */}
        <div className="fixed top-0 left-0 right-0 z-20 md:static md:z-auto">
          <Navbar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        </div>

        {/* Espace réservé pour la navbar fixe sur mobile (64px) */}
        <div className="h-16 flex-shrink-0 md:hidden" />

        <main
          className="flex-1 overflow-y-auto p-4 md:p-6"
          style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}
        >
          <AppRoutes />
        </main>
      </div>
    </div>
  );
};

const App = () => (
  <BrowserRouter>
    <AppShell />
    <ToastContainer />
  </BrowserRouter>
);

export default App;
